import React from 'react'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../ui/table'
import Pagination from './Pagination'
import DefaultTableHeader from './DefaultTableHeader'
import { useBusiness } from '../../../context/BusinessContext'
import { TriangleAlert } from 'lucide-react'

interface Column {
    key: string
    label: string
    render?: (value: any, row: any) => React.ReactNode
}

interface DataTableProps {
    columns: Column[]
    rows: any[]
    rowsPerPage?: number
    handleClick?: (row: any) => void
    handleCreate?: () => void
    handleBusinessDetail?: boolean
}

export default function DataTable({
    columns,
    rows,
    rowsPerPage = 20,
    handleClick,
    handleCreate,
    handleBusinessDetail = false
}: DataTableProps) {
    const { setOpenDetail } = useBusiness()
    const [searchTerm, setSearchTerm] = React.useState('')
    const [filteredRows, setFilteredRows] = React.useState(rows)
    const [currentPage, setCurrentPage] = React.useState(1)

    //? Busca
    React.useEffect(() => {
        const filtered = rows.filter(row =>
            Object.values(row).some(
                value => typeof value === 'string' && value.toLowerCase().includes(searchTerm.toLowerCase())
            )
        )
        setFilteredRows(filtered)
        setCurrentPage(1) // Reset to first page on search
    }, [searchTerm, rows])

    //* Paginação
    const indexOfLastRow = currentPage * rowsPerPage
    const indexOfFirstRow = indexOfLastRow - rowsPerPage
    const currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow)
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage)

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    return (
        <div>
            {/* Desktop Table */}
            <div className='hidden md:block'>
                <div className='max-w-full overflow-x-auto'>
                    <DefaultTableHeader handleChange={setSearchTerm} handleCreate={handleCreate} />

                    {/* Table Body */}
                    {currentRows.length > 0 ? (
                        <>
                            <Table>
                                {/* Table Header */}
                                <TableHeader className='border-b border-gray-100 dark:border-white/[0.05]'>
                                    <TableRow>
                                        {columns.map(column => (
                                            <TableCell
                                                key={column.key}
                                                isHeader
                                                className='px-5 py-3 font-bold text-gray-500 text-start text-theme-xs dark:text-gray-400'
                                            >
                                                {column.label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody className='divide-y divide-gray-100 dark:divide-white/[0.05]'>
                                    {currentRows.map((row, rowIndex) => {
                                        return (
                                            <TableRow
                                                key={rowIndex}
                                                onClick={() => {
                                                    handleClick && handleClick(row)
                                                    handleBusinessDetail &&
                                                        setOpenDetail({
                                                            open: true,
                                                            data: row
                                                        })
                                                }}
                                                className='hover:bg-gray-100 dark:hover:bg-white/[0.03] text-red-500 transition ease-in-out duration-200'
                                            >
                                                {columns.map(column => (
                                                    <TableCell
                                                        key={column.key}
                                                        className='px-4 py-3 text-theme-sm text-gray-800 dark:text-white/90 cursor-pointer '
                                                    >
                                                        {column.render
                                                            ? column.render(row[column.key], row)
                                                            : row[column.key]}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                            <div className='p-4'>
                                <Pagination
                                    total={filteredRows.length}
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    handlePageChange={handlePageChange}
                                />
                            </div>
                        </>
                    ) : (
                        <div className='mt-4 flex items-center gap-2'>
                            <TriangleAlert className='w-5 h-5 text-warning-500' />
                            <p className='text-sm dark:text-gray-400'>Nenhum resultado encontrado!</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile Cards */}
            <div className='block md:hidden space-y-2 '>
                <DefaultTableHeader handleChange={setSearchTerm} handleCreate={handleCreate} />
                {currentRows.map((row, rowIndex) => (
                    <div
                        key={rowIndex}
                        className='p-4 mb-4 rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]'
                        onClick={() => handleClick && handleClick(row)}
                    >
                        {columns.map(column => (
                            <div key={column.key} className='mb-3'>
                                <span className='text-gray-500 dark:text-gray-400'>
                                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                                </span>
                            </div>
                        ))}
                    </div>
                ))}
                <Pagination
                    total={filteredRows.length}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />
            </div>
        </div>
    )
}
